import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {WRITING_COACH_CONTENT_DESCRIPTOR} from "@khan/assignments-utils";
import {useShowAssignableKhanmigoActivities} from "@khan/class-ui";
import {isActivitiesPage} from "@khan/guide-core-helpers";
import {useGuideState} from "@khan/guide-state";
import {mediaQueries} from "@khan/shared-styles";
import {useLocation} from "@khan/urls";

import {AsStudentToggle} from "./as-student-toggle.tsx";
import CreateAssignmentButton from "./create-activity-assignment-button.tsx";
import GuideDisclaimer from "./guide-disclaimer.tsx";
import {HEADER_HEIGHT} from "./header/constants.ts";
import type {HeaderType} from "./header/types.ts";
import {usePropsForHeaderType} from "./header/use-props-for-header-type.ts";

const BORDER_HEIGHT = 1;

interface Props {
    activityAssignable: boolean;
    activityContentDescriptor: string | null | undefined;
    type: HeaderType.DesktopActivity;
    hideGuideDisclaimer?: boolean;
}

/**
 * A desktop activities-only subheader for Khanmigo.
 *
 * @param props
 * @returns {JSX.Element}
 */
export const Subheader = (props: Props): React.ReactElement => {
    const threadId = useGuideState("threadId");
    const {pathname} = useLocation();
    const {isChatHeader, showKhanmigoLabel, showStudentToggle} =
        usePropsForHeaderType(props.type);
    const {activityAssignable, activityContentDescriptor} = props;

    const assignableKMActivitiesIsOn = useShowAssignableKhanmigoActivities();

    const activityIsWritingCoach =
        activityContentDescriptor === WRITING_COACH_CONTENT_DESCRIPTOR;

    const showCreateAssignmentButton =
        assignableKMActivitiesIsOn.status === "success" &&
        assignableKMActivitiesIsOn.data &&
        activityAssignable &&
        !activityIsWritingCoach;

    // HACK(casting to any was the only way I could get TS to pass this check)
    const chatHeaderStyle: any = [
        styles.header,
        isChatHeader && styles.chatHeader,
        styles.desktopActivityHeader,
        styles.subheaderBottom,
    ];

    if (!showCreateAssignmentButton || !activityContentDescriptor) {
        return props.hideGuideDisclaimer ? <></> : <GuideDisclaimer />;
    }

    return (
        <View
            style={chatHeaderStyle}
            testId="chat-bot-subheader-with-assign-button"
        >
            {/* HACK: Hidden element to match css styling and flex spacing of Header component */}
            <View style={styles.hiddenElement}>
                {showKhanmigoLabel && (
                    <View style={styles.headerGroup}>
                        <View
                            style={[
                                styles.khanmigoLabel,
                                // On activities pages, in portrait mode, tablets don't have enough
                                // screen space to show both the label and longer activity titles.
                                isActivitiesPage(pathname) &&
                                    styles.hideKhanmigoLabelForSmallScreens,
                            ]}
                        >
                            <LabelLarge>{t`Khanmigo`}</LabelLarge>
                            {/*TODO (GL-838) Replace with WB Pill component when available.*/}
                            <View style={styles.betaLabel}>{t`BETA`}</View>
                        </View>
                        {showStudentToggle && (
                            <AsStudentToggle threadId={threadId} />
                        )}
                    </View>
                )}
            </View>
            {!props.hideGuideDisclaimer && (
                <GuideDisclaimer style={styles.disclaimer} />
            )}
            <View style={!showCreateAssignmentButton && styles.hiddenElement}>
                <CreateAssignmentButton
                    size="small"
                    contentDescriptor={activityContentDescriptor}
                />
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    betaLabel: {
        fontSize: 8,
        fontWeight: "bold",
        color: color.offWhite,
        marginBlockEnd: spacing.xSmall_8,
    },
    chatHeader: {
        borderStartStartRadius: 8,
        borderStartEndRadius: 8,
    },
    header: {
        flexDirection: "row",
        flexWrap: "nowrap",
        flexShrink: 0,
        height: HEADER_HEIGHT,
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center",
        color: color.offWhite,
        /* As designed */
        padding: "9px 8px",
        paddingInlineStart: spacing.medium_16,
    },
    headerGroup: {
        flexDirection: "row",
        alignItems: "center",
    },
    hideKhanmigoLabelForSmallScreens: {
        [mediaQueries.mdOrSmaller]: {
            display: "none",
        },
    },
    khanmigoLabel: {
        flexDirection: "row",
    },
    desktopActivityHeader: {
        [mediaQueries.mdOrLarger]: {
            padding: "9px 32px",
        },
    },
    disclaimer: {
        border: "none",
    },
    hiddenElement: {
        flexDirection: "row",
        alignItems: "center",
        visibility: "hidden",
    },
    subheaderBottom: {
        borderBlockEnd: `${BORDER_HEIGHT}px solid ${color.offBlack8}`,
    },
});
